import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Plus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface BankAccountFormProps {
  accountId?: string;
  initialData?: any;
  onSuccess?: () => void;
  trigger?: React.ReactNode;
}

export const BankAccountForm: React.FC<BankAccountFormProps> = ({ accountId, initialData, onSuccess, trigger }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState('checking');
  const [iban, setIban] = useState('');
  const [swift, setSwift] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [openingBalance, setOpeningBalance] = useState('0');
  const [bankName, setBankName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [notes, setNotes] = useState('');

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const saveAccountMutation = useMutation({
    mutationFn: async ({ accountId: id, data }: { accountId?: string; data: any }) => {
      if (id) {
        const { data: updated, error } = await supabase
          .from('bank_accounts')
          .update(data)
          .eq('id', id)
          .select()
          .single();
        if (error) throw error;
        return updated;
      } else {
        const { data: created, error } = await supabase
          .from('bank_accounts')
          .insert(data)
          .select()
          .single();
        if (error) throw error;
        return created;
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
      toast({ title: variables?.accountId ? 'Bank account updated successfully!' : 'Bank account created successfully!' });
      setOpen(false);
      resetForm();
      onSuccess?.();
    },
    onError: (error: any) => {
      toast({
        title: 'Error saving bank account',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  const resetForm = () => {
    setName('');
    setType('checking');
    setIban('');
    setSwift('');
    setCurrency('EUR');
    setOpeningBalance('0');
    setBankName('');
    setIsActive(true);
    setNotes('');
  };

  // Prefill form when editing
  useEffect(() => {
    if (!open) return;
    if (initialData) {
      setName(initialData.name || '');
      setType(initialData.type || 'checking');
      setIban(initialData.iban || '');
      setSwift(initialData.swift || '');
      setCurrency(initialData.currency || 'EUR');
      setOpeningBalance(String(initialData.opening_balance ?? '0'));
      setBankName(initialData.bank_name || '');
      setIsActive(initialData.is_active ?? true);
      setNotes(initialData.notes || '');
    } else if (!accountId) {
      resetForm();
    }
  }, [open, initialData, accountId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Account name is required',
        variant: 'destructive'
      });
      return;
    }

    const accountData = {
      name: name.trim(),
      type,
      iban: iban.trim() || null,
      swift: swift.trim() || null,
      currency,
      opening_balance: parseFloat(openingBalance) || 0,
      bank_name: bankName.trim() || null,
      is_active: isActive,
      notes: notes.trim() || null
    };
    saveAccountMutation.mutate({ accountId, data: accountData });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New Account
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{accountId ? 'Edit Bank Account' : 'Create New Bank Account'}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Account Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Main Checking Account"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">Account Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="checking">Checking Account</SelectItem>
                <SelectItem value="savings">Savings Account</SelectItem>
                <SelectItem value="credit">Credit Account</SelectItem>
                <SelectItem value="cash">Cash Account</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="RON">RON (lei)</SelectItem>
                  <SelectItem value="PLN">PLN (zł)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="openingBalance">Opening Balance</Label>
              <Input
                id="openingBalance"
                type="number"
                value={openingBalance}
                onChange={(e) => setOpeningBalance(e.target.value)}
                step="0.01"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bankName">Bank Name</Label>
            <Input
              id="bankName"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="e.g., National Bank of Greece"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="iban">IBAN</Label>
            <Input
              id="iban"
              value={iban}
              onChange={(e) => setIban(e.target.value.toUpperCase())}
              placeholder="GR1601101250000000012300695"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="swift">SWIFT/BIC</Label>
            <Input
              id="swift"
              value={swift}
              onChange={(e) => setSwift(e.target.value.toUpperCase())}
              placeholder="ETHNGRAA"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="isActive"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
            <Label htmlFor="isActive">Active Account</Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes about this account..."
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saveAccountMutation.isPending}>
              {saveAccountMutation.isPending ? 'Saving...' : (accountId ? 'Save Changes' : 'Create Account')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};